import { createSignal, createResource, createEffect, createMemo, children, For, Show } from 'solid-js';
import { formatNumber, parseNumericInput } from '../lib/money.js';

// Karyawan — employee master + per-employee salary variables.
// Variables are the values var("...") reads in scheme formulas; editing one
// affects the NEXT pay run only (runs snapshot what they read).

async function getJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('HTTP ' + res.status);
  return res.json();
}

const STATUS_BADGE = { Tetap: 'success', Kontrak: 'info', Keluarga: 'warning' };

// todayISO — today's date as YYYY-MM-DD in LOCAL time (not UTC).
function todayISO() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}
const fmtISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function addDaysISO(iso, days) { const d = new Date(iso + 'T00:00:00'); d.setDate(d.getDate() + days); return fmtISO(d); }
function addYearsISO(iso, years) { const d = new Date(iso + 'T00:00:00'); d.setFullYear(d.getFullYear() + years); return fmtISO(d); }

// daysUntil — whole days from today to an ISO date (negative = past); null on bad input.
function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d)) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}

// ContractBadge — expiry status for a contract end date.
function ContractBadge(props) {
  const n = () => daysUntil(props.date);
  return (
    <Show when={n() !== null}>
      <Show when={n() < 0} fallback={
        <span class={`badge ${n() <= 30 ? 'warning' : 'success'}`}>
          {n() === 0 ? 'berakhir hari ini' : `berakhir dalam ${n()} hari`}
        </span>
      }>
        <span class="badge danger">kadaluarsa {Math.abs(n())} hari lalu</span>
      </Show>
    </Show>
  );
}

export default function Karyawan() {
  const [q, setQ] = createSignal('');
  const [scheme, setScheme] = createSignal('');
  const [activeFilter, setActiveFilter] = createSignal('aktif'); // aktif | nonaktif | semua
  const [dept, setDept] = createSignal(''); // department NAME filter (client-side; '__none__' = no dept)
  const [divisi, setDivisi] = createSignal(''); // division CODE filter (client-side; '__none__' = no divisi)
  const [selected, setSelected] = createSignal('');

  // Debounce the search box so we don't query per keystroke.
  const [query, setQuery] = createSignal('');
  let qTimer;
  createEffect(() => {
    const v = q();
    clearTimeout(qTimer);
    qTimer = setTimeout(() => setQuery(v), 250);
  });

  const [list, { refetch: refetchList }] = createResource(
    () => `q=${encodeURIComponent(query())}&scheme=${scheme()}&active=${activeFilter()}`,
    (params) => getJSON(`/api/employees?${params}`),
  );

  // Grand total for the current active-filter scope, ignoring the search + scheme
  // filters, so the header can show "{filtered} dari {total}" when a filter narrows
  // the list. Keyed on activeFilter only — that changes the universe, while
  // search/scheme merely narrow it. Refetched alongside the list when the roster
  // changes (create/import/resign).
  const [total, { refetch: refetchTotal }] = createResource(
    activeFilter,
    (active) => getJSON(`/api/employees?q=&scheme=&active=${active}`).then((r) => (r ?? []).length),
  );

  // Scheme filter options come from the live scheme list (was hardcoded to
  // OFFICE/SALES, hiding TKBM/MANAGERIAL). Company-scoped on the server.
  const [filterSchemes] = createResource(() => getJSON('/api/schemes').catch(() => []));
  // Department masters (for the bulk "Departemen" dropdown — needs codes).
  const [depts] = createResource(() => getJSON('/api/departments').catch(() => []));
  // Division masters (for the Divisi filter dropdown).
  const [divisions] = createResource(() => getJSON('/api/divisions').catch(() => []));

  // ── Bulk selection (checkbox column) + bulk action state ───────────────
  const [checked, setChecked] = createSignal(new Set()); // selected NIKs
  const [bulkActive, setBulkActive] = createSignal('');  // '' = Tidak Berubah | aktif | nonaktif
  const [bulkScheme, setBulkScheme] = createSignal('');  // '' = Tidak Berubah | <code>
  const [bulkDept, setBulkDept] = createSignal('');      // '' = Tidak Berubah | <dept code>
  const [bulkBusy, setBulkBusy] = createSignal(false);
  const [bulkMsg, setBulkMsg] = createSignal(null);

  // Client-side column sort over the loaded list.
  const [sortKey, setSortKey] = createSignal('nik');
  const [sortDir, setSortDir] = createSignal('asc');
  function toggleSort(key) {
    if (sortKey() === key) setSortDir(sortDir() === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }
  const sortArrow = (key) => (sortKey() === key ? (sortDir() === 'asc' ? ' ▲' : ' ▼') : '');
  const sortedList = createMemo(() => {
    const rows = (list() ?? []).slice();
    const k = sortKey(), dir = sortDir() === 'asc' ? 1 : -1;
    const field = (e) => String(e[k] ?? '');
    rows.sort((a, b) => field(a).localeCompare(field(b), 'id', { numeric: true, sensitivity: 'base' }) * dir);
    return rows;
  });

  // Department filter options derived from the loaded list (guaranteed to match
  // existing data); the filter itself is applied client-side over sortedList.
  const deptNames = createMemo(() => {
    const s = new Set();
    for (const e of list() ?? []) if (e.department) s.add(e.department);
    return [...s].sort();
  });
  // divisionCode → display name (the employee row carries only the code).
  const divByCode = createMemo(() => {
    const m = {};
    for (const d of divisions() ?? []) m[d.code] = d.name;
    return m;
  });
  const divName = (code) => (code ? (divByCode()[code] || code) : '');
  const visibleRows = createMemo(() => {
    const d = dept(), v = divisi();
    let rows = sortedList();
    if (d) rows = rows.filter((e) => (d === '__none__' ? !e.department : e.department === d));
    if (v) rows = rows.filter((e) => (v === '__none__' ? !e.divisionCode : e.divisionCode === v));
    return rows;
  });

  // Clear the bulk selection whenever the visible set changes (search, scheme,
  // active, department, divisi) — so an Apply can never hit rows you can't see.
  createEffect(() => {
    query(); scheme(); activeFilter(); dept(); divisi();
    setChecked(new Set());
  });

  const toggleCheck = (nik, on) => {
    const s = new Set(checked());
    on ? s.add(nik) : s.delete(nik);
    setChecked(s);
  };
  const allChecked = createMemo(() => {
    const rows = visibleRows();
    return rows.length > 0 && rows.every((e) => checked().has(e.nik));
  });
  function toggleAll(on) {
    const s = new Set(checked());
    for (const e of visibleRows()) on ? s.add(e.nik) : s.delete(e.nik);
    setChecked(s);
  }
  const bulkDirty = () => bulkActive() || bulkScheme() || bulkDept();

  async function applyBulk() {
    const niks = [...checked()];
    if (niks.length === 0 || !bulkDirty()) return;
    let setEndDate = false;
    if (bulkActive() === 'nonaktif') {
      // Per HR: ask whether to also stamp the resignation (keluar) date.
      setEndDate = window.confirm(
        `Nonaktifkan ${niks.length} karyawan.\n\n` +
        `OK = set tanggal keluar (resign) ke hari ini.\n` +
        `Batal = nonaktifkan saja tanpa tanggal keluar.`);
    } else if (!window.confirm(`Terapkan perubahan ke ${niks.length} karyawan?`)) {
      return;
    }
    setBulkBusy(true); setBulkMsg(null);
    const res = await fetch('/api/employees/bulk', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ niks, active: bulkActive(), setEndDate, scheme: bulkScheme(), department: bulkDept() }),
    });
    const data = await res.json().catch(() => ({}));
    setBulkBusy(false);
    if (res.ok) {
      setBulkMsg({ ok: true, t: `${data.updated} karyawan diperbarui.` });
      setChecked(new Set()); setBulkActive(''); setBulkScheme(''); setBulkDept('');
      refetchList(); refetchTotal(); refetchDetail();
    } else setBulkMsg({ ok: false, t: data.error || `HTTP ${res.status}` });
  }

  const [detail, { refetch: refetchDetail }] = createResource(
    selected,
    (nik) => nik ? getJSON(`/api/employee/${encodeURIComponent(nik)}`) : null,
  );

  // Create/edit form overlay: null | { mode: 'new' } | { mode: 'edit' }.
  const [formMode, setFormMode] = createSignal(null);
  // Kelola kontrak modal: null | the employee object.
  const [contractEmp, setContractEmp] = createSignal(null);

  // ── Export to Excel (toggleable columns, remembered) ───────────────────
  const [exportOpen, setExportOpen] = createSignal(false);
  const [exportCols] = createResource(() => getJSON('/api/employees/export-columns'));
  const [excludedCols, setExcludedCols] = createSignal(new Set());
  const EXPORT_KEY = 'karyawan_export_cols';
  const isColIn = (key) => !excludedCols().has(key);
  const includedColCount = () => (exportCols() ?? []).filter((c) => isColIn(c.key)).length;
  function persistCols(s) {
    try { localStorage.setItem(EXPORT_KEY, JSON.stringify([...s])); } catch (_) {}
  }
  function openExport() {
    let saved = [];
    try { saved = JSON.parse(localStorage.getItem(EXPORT_KEY) || '[]'); } catch (_) {}
    setExcludedCols(new Set(saved));
    setExportOpen(true);
  }
  function toggleCol(key, on) {
    const s = new Set(excludedCols());
    on ? s.delete(key) : s.add(key);
    setExcludedCols(s);
    persistCols(s);
  }
  function selectAllCols(on) {
    const s = on ? new Set() : new Set((exportCols() ?? []).map((c) => c.key));
    setExcludedCols(s);
    persistCols(s);
  }
  function doExport() {
    const keys = (exportCols() ?? []).map((c) => c.key).filter(isColIn);
    const params = new URLSearchParams({
      cols: keys.join(','), q: query(), scheme: scheme(), active: activeFilter(),
    });
    const a = document.createElement('a');
    a.href = `/api/employees/export?${params.toString()}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setExportOpen(false);
  }

  // ── Import from Excel (template → preview → apply) ──────────────────────
  const [importOpen, setImportOpen] = createSignal(false);
  const [importFile, setImportFile] = createSignal(null);
  const [importMode, setImportMode] = createSignal('upsert');     // upsert | insert
  const [importUnknownOrg, setImportUnknownOrg] = createSignal('keep'); // keep | reject
  const [importDateFormat, setImportDateFormat] = createSignal('YYYY-MM-DD');
  const [importPreview, setImportPreview] = createSignal(null);
  const [importBusy, setImportBusy] = createSignal(false);
  const [importMsg, setImportMsg] = createSignal(null);
  let importFileInput;
  function openImport() {
    setImportFile(null); setImportPreview(null); setImportMsg(null);
    setImportMode('upsert'); setImportUnknownOrg('keep'); setImportDateFormat('YYYY-MM-DD');
    if (importFileInput) importFileInput.value = '';
    setImportOpen(true);
  }
  async function runImport(dryRun) {
    const f = importFile();
    if (!f) return;
    setImportBusy(true); setImportMsg(null);
    const params = new URLSearchParams({
      dryRun: dryRun ? '1' : '0', mode: importMode(), unknownOrg: importUnknownOrg(),
      dateFormat: importDateFormat(),
    });
    const fd = new FormData();
    fd.set('file', f);
    const res = await fetch(`/api/employees/import?${params.toString()}`, { method: 'POST', body: fd });
    const data = await res.json().catch(() => ({}));
    setImportBusy(false);
    if (!res.ok) { setImportPreview(data.preview ?? null); setImportMsg({ ok: false, t: data.error || 'gagal' }); return; }
    setImportPreview(data);
    if (data.applied) {
      setImportMsg({ ok: true, t: `Impor selesai — ${data.insert} ditambah, ${data.update} diperbarui, ${data.skip} dilewati.` });
      refetchList(); refetchTotal();
    }
  }
  function pickImportFile(file) {
    setImportFile(file); setImportMsg(null);
    if (file) runImport(true); else setImportPreview(null);
  }
  const importErrRows = () => (importPreview()?.rows ?? []).filter((r) => r.action === 'error');
  const importWarnRows = () => (importPreview()?.rows ?? []).filter((r) => (r.warnings ?? []).length > 0 && r.action !== 'error');

  // ── Resign (mark employee keluar: active=0 + end_date) ─────────────────
  const [resigning, setResigning] = createSignal(false);
  const [resignDate, setResignDate] = createSignal(todayISO());
  const [resignBusy, setResignBusy] = createSignal(false);
  async function doResign() {
    setResignBusy(true);
    const res = await fetch(`/api/employee/${encodeURIComponent(selected())}/resign`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date: resignDate() }),
    });
    setResignBusy(false);
    if (res.ok) { setResigning(false); refetchDetail(); refetchList(); refetchTotal(); }
    else alert((await res.json().catch(() => ({}))).error || 'gagal');
  }

  // Reset the employee's PORTAL password back to the default "karyawan".
  async function resetPassword() {
    const nik = selected();
    const name = detail()?.employee?.name || nik;
    if (!confirm(`Reset password portal ${name} (${nik}) ke default "karyawan"?\n\nKaryawan harus login lalu membuat password baru. Slip & data hanya bisa dibuka setelah password diganti.`)) return;
    const res = await fetch(`/api/employee/${encodeURIComponent(nik)}/reset-password`, { method: 'POST' });
    const data = await res.json().catch(() => ({}));
    if (res.ok) alert(`Password portal ${name} direset ke "karyawan".`);
    else alert(data.error || 'gagal mereset password');
  }

  // ── Variable editing ───────────────────────────────────────────────────
  const [editVar, setEditVar] = createSignal(null); // {name, value} being edited
  const [varMsg, setVarMsg] = createSignal(null);
  const [newVar, setNewVar] = createSignal(false);

  // ── Saldo (ledger-style variables) ─────────────────────────────────────
  const [balances, { refetch: refetchBalances }] = createResource(
    selected,
    (nik) => nik ? getJSON(`/api/employee/${encodeURIComponent(nik)}/balances`) : null,
  );
  const [saldoOpen, setSaldoOpen] = createSignal(false);
  const [saldoInitial, setSaldoInitial] = createSignal(''); // pre-select a balance in the modal
  const openSaldo = (name) => { setSaldoInitial(name || ''); setSaldoOpen(true); };

  async function saveVar(name, value) {
    setVarMsg(null);
    const res = await fetch(`/api/employee/${encodeURIComponent(selected())}/var`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, value }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setVarMsg({ ok: true, msg: `${name} tersimpan.` });
      setEditVar(null);
      setNewVar(false);
      refetchDetail();
    } else {
      setVarMsg({ ok: false, msg: data.error ?? `HTTP ${res.status}` });
    }
  }

  // Remove a per-employee override: the value falls back to the scheme
  // default shown on the Variabel Gaji page.
  async function dropOverride(name) {
    if (!confirm(`Hapus nilai khusus "${name}"? Karyawan ini akan kembali memakai default skema.`)) return;
    setVarMsg(null);
    const res = await fetch(
      `/api/employee/${encodeURIComponent(selected())}/var?name=${encodeURIComponent(name)}`,
      { method: 'DELETE' });
    if (res.ok) {
      setVarMsg({ ok: true, msg: `${name} kembali ke default skema.` });
      refetchDetail();
    } else {
      const data = await res.json().catch(() => ({}));
      setVarMsg({ ok: false, msg: data.error ?? `HTTP ${res.status}` });
    }
  }

  return (
    <>
      <div class="page-head">
        <h2>Karyawan</h2>
        <div class="spacer" />
        <Show when={list()}>
          <span class="kg-meta-text">
            <Show when={total() != null && visibleRows().length !== total()}
                  fallback={`${visibleRows().length} karyawan`}>
              {visibleRows().length} dari {total()} karyawan
            </Show>
          </span>
        </Show>
        <button class="btn" onClick={openExport} title="Export daftar karyawan ke Excel">⤓ Export Excel</button>
        <button class="btn" onClick={openImport} title="Import karyawan dari Excel">⤴ Import Excel</button>
        <button class="btn primary" onClick={() => setFormMode({ mode: 'new' })}>+ Karyawan</button>
      </div>

      <div class="ky-toolbar">
        <input
          class="ky-search"
          type="search"
          placeholder="Cari NIK atau nama…"
          value={q()}
          onInput={(e) => setQ(e.currentTarget.value)}
        />
        <select class="kg-select" value={scheme()} onInput={(e) => setScheme(e.currentTarget.value)}>
          <option value="">Semua skema</option>
          <option value="__none__">Tanpa skema</option>
          <For each={filterSchemes() ?? []}>{(s) => <option value={s.code}>{s.code}</option>}</For>
        </select>
        <select class="kg-select" value={divisi()} onInput={(e) => setDivisi(e.currentTarget.value)}>
          <option value="">Semua divisi</option>
          <option value="__none__">Tanpa divisi</option>
          <For each={divisions() ?? []}>{(d) => <option value={d.code}>{d.code} · {d.name}</option>}</For>
        </select>
        <select class="kg-select" value={dept()} onInput={(e) => setDept(e.currentTarget.value)}>
          <option value="">Semua departemen</option>
          <option value="__none__">Tanpa departemen</option>
          <For each={deptNames()}>{(d) => <option value={d}>{d}</option>}</For>
        </select>
        <select class="kg-select" value={activeFilter()} onInput={(e) => setActiveFilter(e.currentTarget.value)}>
          <option value="aktif">Aktif</option>
          <option value="nonaktif">Tidak Aktif</option>
          <option value="semua">Semua</option>
        </select>
      </div>

      <Show when={bulkMsg()}>
        <div class={`kg-verdict ${bulkMsg().ok ? 'ok' : 'fail'}`} style="margin-bottom:10px">{bulkMsg().t}</div>
      </Show>
      <Show when={checked().size > 0}>
        <div class="card card-pad ky-bulk">
          <b>{checked().size} dipilih</b>
          <label class="kg-field ky-bulk-field"><span>Aktif</span>
            <select class="kg-select" value={bulkActive()} onInput={(e) => setBulkActive(e.currentTarget.value)}>
              <option value="">Tidak Berubah</option>
              <option value="aktif">Aktif</option>
              <option value="nonaktif">Tidak Aktif</option>
            </select>
          </label>
          <label class="kg-field ky-bulk-field"><span>Skema</span>
            <select class="kg-select" value={bulkScheme()} onInput={(e) => setBulkScheme(e.currentTarget.value)}>
              <option value="">Tidak Berubah</option>
              <For each={filterSchemes() ?? []}>{(s) => <option value={s.code}>{s.code}</option>}</For>
            </select>
          </label>
          <label class="kg-field ky-bulk-field"><span>Departemen</span>
            <select class="kg-select" value={bulkDept()} onInput={(e) => setBulkDept(e.currentTarget.value)}>
              <option value="">Tidak Berubah</option>
              <For each={depts() ?? []}>{(d) => <option value={d.code}>{d.code} · {d.name}</option>}</For>
            </select>
          </label>
          <div class="spacer" />
          <button class="btn primary" disabled={bulkBusy() || !bulkDirty()} onClick={applyBulk}>
            {bulkBusy() ? 'Menerapkan…' : 'Terapkan'}
          </button>
          <button class="btn ghost" disabled={bulkBusy()} onClick={() => setChecked(new Set())}>Batal pilih</button>
        </div>
      </Show>

      <div class="ky-layout">
        {/* ── Employee list ───────────────────────────────────────────── */}
        <div class="card ky-list">
          <table class="data">
            <thead>
              <tr class="ky-sort-row">
                <th class="ky-check-cell" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={allChecked()} onInput={(e) => toggleAll(e.currentTarget.checked)} title="Pilih semua yang tampil" />
                </th>
                <th class="sortable" onClick={() => toggleSort('nik')}>NIK{sortArrow('nik')}</th>
                <th class="sortable" onClick={() => toggleSort('name')}>Nama{sortArrow('name')}</th>
                <th class="sortable" onClick={() => toggleSort('department')}>Departemen{sortArrow('department')}</th>
                <th class="sortable" onClick={() => toggleSort('divisionCode')}>Divisi{sortArrow('divisionCode')}</th>
                <th class="sortable" onClick={() => toggleSort('scheme')}>Skema{sortArrow('scheme')}</th>
                <th class="sortable" onClick={() => toggleSort('status')}>Status{sortArrow('status')}</th>
              </tr>
            </thead>
            <tbody>
              <For each={visibleRows()}>{(e) =>
                <tr classList={{ selected: selected() === e.nik, inactive: !e.active, 'ky-picked': checked().has(e.nik) }}
                    onClick={() => { setSelected(e.nik); setVarMsg(null); setEditVar(null); setNewVar(false); setResigning(false); setSaldoOpen(false); }}>
                  <td class="ky-check-cell" onClick={(ev) => ev.stopPropagation()}>
                    <input type="checkbox" checked={checked().has(e.nik)} onInput={(ev) => toggleCheck(e.nik, ev.currentTarget.checked)} />
                  </td>
                  <td class="ky-nik">{e.nik}</td>
                  <td>{e.name}</td>
                  <td>{e.department}</td>
                  <td>{divName(e.divisionCode) || <span class="vg-dim">—</span>}</td>
                  <td>{e.scheme || '—'}</td>
                  <td>
                    <span class={`badge ${STATUS_BADGE[e.status] ?? 'info'}`}>{e.status}</span>
                    <Show when={!e.active}> <span class="badge danger">keluar</span></Show>
                  </td>
                </tr>
              }</For>
            </tbody>
          </table>
        </div>

        {/* ── Detail + variables ──────────────────────────────────────── */}
        <div class="ky-detail">
          <Show when={detail()} fallback={
            <div class="card card-pad"><div class="empty"><div>
              <div class="big">☷</div>
              Pilih karyawan untuk melihat profil dan variabel gajinya.
            </div></div></div>
          }>
            <div class="card card-pad">
              <div class="ky-head">
                <div>
                  <h3 class="ky-name">{detail().employee.name}</h3>
                  <div class="kg-meta-text">
                    {detail().employee.nik}
                    <Show when={detail().employee.scheme}> · {detail().employee.scheme}</Show>
                    <Show when={detail().employee.position}> · {detail().employee.position}</Show>
                  </div>
                </div>
                <div class="spacer" />
                <Show when={detail().employee.active}>
                  <button class="btn ghost" onClick={() => { setResignDate(todayISO()); setResigning(true); }}>Tandai Resign</button>
                </Show>
                <button class="btn ghost" onClick={resetPassword} title='Reset password portal karyawan ke default "karyawan"'>Reset Password</button>
                <button class="btn ghost" onClick={() => setFormMode({ mode: 'edit' })}>Edit profil</button>
                <span class={`badge ${STATUS_BADGE[detail().employee.status] ?? 'info'}`}>
                  {detail().employee.status}
                </span>
                <Show when={!detail().employee.active}><span class="badge danger">keluar</span></Show>
              </div>

              <Show when={resigning()}>
                <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin:8px 0">
                  <span class="kg-meta-text">Tandai <b>{detail().employee.name}</b> resign per tanggal</span>
                  <input type="date" value={resignDate()} onInput={(e) => setResignDate(e.currentTarget.value)} />
                  <button class="btn primary" disabled={resignBusy()} onClick={doResign}>
                    {resignBusy() ? 'Memproses…' : 'Konfirmasi Resign'}</button>
                  <button class="btn ghost" disabled={resignBusy()} onClick={() => setResigning(false)}>Batal</button>
                </div>
              </Show>

              <div class="ky-facts">
                <div><span>Departemen</span>{detail().employee.department || '—'}</div>
                <div><span>Masuk</span>{detail().employee.hireDate || '—'}</div>
                <Show when={detail().employee.endDate}>
                  <div><span>Keluar</span>{detail().employee.endDate}</div>
                </Show>
                <div><span>Bank</span>{detail().employee.bankName
                  ? `${detail().employee.bankName} · ${detail().employee.bankAccount}` : '—'}</div>
              </div>

              <div class="ky-vars-head">
                <h4 class="kg-hist-title">Kontrak & Profil</h4>
                <div class="spacer" />
                <button class="btn ghost" onClick={() => setContractEmp(detail().employee)}>Kelola kontrak</button>
              </div>
              <div class="ky-facts">
                <div><span>Status</span>
                  <span class={`badge ${STATUS_BADGE[detail().employee.status] ?? 'info'}`}>{detail().employee.status}</span>
                </div>
                <div><span>Tgl Lahir</span>{detail().employee.birthDate
                  ? `${detail().employee.birthDate}${detail().employee.birthPlace ? ' · ' + detail().employee.birthPlace : ''}` : '—'}</div>
                <Show when={detail().employee.appointmentDate}>
                  <div><span>Pengangkatan</span>{detail().employee.appointmentDate}</div>
                </Show>
                <Show when={detail().employee.contractStart}>
                  <div><span>Kontrak Mulai</span>{detail().employee.contractStart}</div>
                </Show>
                <div><span>Kontrak Selesai</span>
                  <Show when={detail().employee.contractEnd} fallback={<>—</>}>
                    {detail().employee.contractEnd} <ContractBadge date={detail().employee.contractEnd} />
                  </Show>
                </div>
              </div>

              <div class="ky-vars-head">
                <h4 class="kg-hist-title">Persetujuan Lembur</h4>
              </div>
              <ApprovalEditor employee={detail().employee} onSaved={refetchDetail} />

              <div class="ky-vars-head">
                <h4 class="kg-hist-title">Variabel gaji</h4>
                <div class="spacer" />
                <button class="btn ghost" onClick={() => { setNewVar(!newVar()); setEditVar(null); }}>
                  {newVar() ? 'batal' : '+ variabel'}
                </button>
              </div>
              <p class="ky-vars-note">
                Nilai ini dibaca formula lewat <code>var("nama")</code>. Perubahan
                berlaku untuk proses gaji BERIKUTNYA — pemrosesan gaji lama menyimpan salinan
                nilainya sendiri.
              </p>

              <Show when={varMsg()}>
                <div class={`kg-verdict ${varMsg().ok ? 'ok' : 'fail'}`}>{varMsg().msg}</div>
              </Show>

              <Show when={newVar()}>
                <VarRowEdit name="" value="" isNew onSave={saveVar} onCancel={() => setNewVar(false)} />
              </Show>

              <table class="data ky-vars">
                <tbody>
                  {/* Saldo (Simpanan/Pinjaman) rows at the top — green simpanan, red pinjaman */}
                  <For each={balances() ?? []}>{(b) =>
                    <tr class="ky-saldo-row">
                      <td>
                        {b.name}{' '}
                        <span class={`badge ${b.kind === 'pinjaman' ? 'danger' : 'success'}`}>{b.kind}</span>
                      </td>
                      <td class="num">
                        <span class={`money ${b.kind === 'simpanan' ? 'earning' : 'deduction'}`}><b>{formatNumber(b.saldo)}</b></span>
                      </td>
                      <td class="num ky-var-actions">
                        <button class="btn ghost" onClick={() => openSaldo(b.name)}>kelola</button>
                      </td>
                    </tr>
                  }</For>
                  <For each={detail().vars}>{(v) =>
                    <Show when={editVar()?.name !== v.name} fallback={
                      <tr><td colspan="3">
                        <VarRowEdit name={v.name} value={v.value} type={v.type} choices={v.choices} onSave={saveVar} onCancel={() => setEditVar(null)} />
                      </td></tr>
                    }>
                      <tr classList={{ 'ky-inherited': v.inherited }}>
                        <td>
                          {v.name}
                          <Show when={v.inherited}>
                            {' '}<span class="badge info" title="nilai default skema — ubah untuk membuat nilai khusus karyawan ini">default</span>
                          </Show>
                        </td>
                        <td class="num">
                          <Show when={v.type === 'enum'} fallback={
                            <span class="money">{formatNumber(v.value)}<Show when={v.unit}><span class="ky-unit">{v.unit}</span></Show></span>
                          }>
                            <span class="badge info">{v.value || '—'}</span>
                          </Show>
                        </td>
                        <td class="num ky-var-actions">
                          <button class="btn ghost" onClick={() => { setEditVar(v); setNewVar(false); }}>ubah</button>
                          <Show when={!v.inherited && detail().employee.scheme}>
                            <button class="btn ghost" title="kembali ke default skema" onClick={() => dropOverride(v.name)}>✕</button>
                          </Show>
                        </td>
                      </tr>
                    </Show>
                  }</For>
                </tbody>
              </table>
              <Show when={detail().vars.length === 0 && (balances() ?? []).length === 0 && !newVar()}>
                <p class="kg-meta-text">Belum ada variabel.</p>
              </Show>
            </div>
          </Show>
        </div>
      </div>

      <Show when={saldoOpen()}>
        <SaldoModal
          nik={selected()}
          balances={balances() ?? []}
          initial={saldoInitial()}
          onClose={() => setSaldoOpen(false)}
          onChanged={() => { refetchBalances(); }}
        />
      </Show>

      <Show when={formMode()}>
        <EmployeeForm
          mode={formMode().mode}
          employee={formMode().mode === 'edit' ? detail()?.employee : null}
          onCancel={() => setFormMode(null)}
          onSaved={(nik) => { setFormMode(null); refetchList(); refetchTotal(); setSelected(nik); refetchDetail(); }}
        />
      </Show>

      <Show when={contractEmp()}>
        <ContractModal
          employee={contractEmp()}
          onCancel={() => setContractEmp(null)}
          onSaved={() => { setContractEmp(null); refetchDetail(); refetchList(); }}
        />
      </Show>

      <Show when={exportOpen()}>
        <div class="modal-backdrop" onClick={() => setExportOpen(false)}>
          <div class="modal-card card card-pad" onClick={(e) => e.stopPropagation()}>
            <div class="ky-vars-head">
              <h4 class="kg-hist-title">Export Karyawan — Excel</h4>
              <div class="spacer" />
              <button class="btn ghost" onClick={() => setExportOpen(false)}>✕</button>
            </div>
            <p class="ky-vars-note">
              Pilih kolom yang ikut diekspor. Pilihan ini <b>diingat</b>. Ekspor mengikuti filter
              aktif (pencarian, skema, hanya aktif) dengan judul & baris header yang dibekukan.
            </p>
            <div class="export-actions">
              <button class="btn ghost" onClick={() => selectAllCols(true)}>Pilih semua</button>
              <button class="btn ghost" onClick={() => selectAllCols(false)}>Kosongkan</button>
              <div class="spacer" />
              <span class="kg-meta-text">{includedColCount()} / {(exportCols() ?? []).length} kolom</span>
            </div>
            <div class="export-comp-list">
              <For each={exportCols() ?? []}>{(c) =>
                <label class="ky-check export-comp">
                  <input type="checkbox" checked={isColIn(c.key)} onInput={(e) => toggleCol(c.key, e.currentTarget.checked)} />
                  {c.label}
                </label>
              }</For>
            </div>
            <div class="export-foot">
              <div class="spacer" />
              <button class="btn ghost" onClick={() => setExportOpen(false)}>Batal</button>
              <button class="btn primary" onClick={doExport}>⤓ Export Excel</button>
            </div>
          </div>
        </div>
      </Show>

      <Show when={importOpen()}>
        <div class="modal-backdrop" onClick={() => setImportOpen(false)}>
          <div class="modal-card card card-pad" onClick={(e) => e.stopPropagation()}>
            <div class="ky-vars-head">
              <h4 class="kg-hist-title">Import Karyawan — Excel</h4>
              <div class="spacer" />
              <button class="btn ghost" onClick={() => setImportOpen(false)}>✕</button>
            </div>
            <p class="ky-vars-note">
              Unduh template, isi (kolom <b>NIK</b> & <b>Nama</b> wajib), pilih
              <b> format tanggal</b> sesuai isi file, lalu unggah. Pratinjau ditampilkan
              sebelum disimpan; impor mengikuti perusahaan aktif Anda.
            </p>
            <div class="export-actions">
              <a class="btn ghost" href="/api/employees/import-template">⤓ Unduh Template</a>
              <div class="spacer" />
              <label class="kg-field" style="margin:0"><span>Format tanggal</span>
                <select class="kg-select" value={importDateFormat()}
                  onInput={(e) => { setImportDateFormat(e.currentTarget.value); if (importFile()) runImport(true); }}>
                  <option value="YYYY-MM-DD">Tahun-Bulan-Hari (2021-06-03)</option>
                  <option value="DD/MM/YYYY">Hari-Bulan-Tahun (03/06/2021)</option>
                  <option value="MM/DD/YYYY">Bulan-Hari-Tahun (06/03/2021)</option>
                </select>
              </label>
              <label class="kg-field" style="margin:0"><span>Mode</span>
                <select class="kg-select" value={importMode()}
                  onInput={(e) => { setImportMode(e.currentTarget.value); if (importFile()) runImport(true); }}>
                  <option value="upsert">Perbarui + tambah baru</option>
                  <option value="insert">Hanya tambah baru</option>
                </select>
              </label>
            </div>
            <label class="kg-field"><span>Berkas .xlsx</span>
              <input type="file" accept=".xlsx" ref={importFileInput}
                onInput={(e) => pickImportFile(e.currentTarget.files[0] || null)} /></label>

            <Show when={importPreview()}>
              <Show when={(importPreview().unknownDepartments?.length || importPreview().unknownPositions?.length)}>
                <div class="ky-vars-note">
                  <b>Departemen/Posisi tak dikenal</b> di file:
                  <Show when={importPreview().unknownDepartments?.length}>
                    <div class="kg-sub">Departemen: {importPreview().unknownDepartments.join(', ')}</div></Show>
                  <Show when={importPreview().unknownPositions?.length}>
                    <div class="kg-sub">Posisi: {importPreview().unknownPositions.join(', ')}</div></Show>
                  <label class="kg-field" style="margin-top:6px"><span>Tindakan untuk yang tak dikenal</span>
                    <select class="kg-select" value={importUnknownOrg()}
                      onInput={(e) => { setImportUnknownOrg(e.currentTarget.value); runImport(true); }}>
                      <option value="keep">Simpan nama apa adanya</option>
                      <option value="reject">Tolak baris tersebut</option>
                    </select></label>
                </div>
              </Show>

              <div class="export-actions">
                <span class="badge success">+{importPreview().insert} tambah</span>
                <span class="badge info">{importPreview().update} perbarui</span>
                <span class="badge warning">{importPreview().skip} lewati</span>
                <span class={`badge ${importPreview().errors ? 'danger' : 'info'}`}>{importPreview().errors} error</span>
                <div class="spacer" />
                <span class="kg-meta-text">dari {importPreview().total} baris</span>
              </div>

              <Show when={importErrRows().length}>
                <div class="export-comp-list">
                  <For each={importErrRows()}>{(r) =>
                    <div class="kg-verdict fail" style="margin:2px 0">Baris {r.line} · {r.nik}: {(r.errors || []).join('; ')}</div>
                  }</For>
                </div>
              </Show>
              <Show when={importWarnRows().length}>
                <div class="export-comp-list">
                  <For each={importWarnRows()}>{(r) =>
                    <div class="kg-meta-text">Baris {r.line} · {r.nik}: {(r.warnings || []).join('; ')}</div>
                  }</For>
                </div>
              </Show>
            </Show>

            <Show when={importMsg()}><div class={`kg-verdict ${importMsg().ok ? 'ok' : 'fail'}`}>{importMsg().t}</div></Show>

            <div class="export-foot">
              <div class="spacer" />
              <button class="btn ghost" onClick={() => setImportOpen(false)}>Tutup</button>
              <button class="btn primary"
                disabled={importBusy() || !importPreview() || importPreview().errors > 0
                  || (importPreview().insert + importPreview().update) === 0 || importPreview().applied}
                onClick={() => runImport(false)}>
                {importBusy() ? 'Memproses…' : '⤴ Impor sekarang'}
              </button>
            </div>
          </div>
        </div>
      </Show>
    </>
  );
}

function VarRowEdit(props) {
  const [name, setName] = createSignal(props.name);
  const [value, setValue] = createSignal(props.value);
  // Enum variables (defined in Variabel Gaji) override via a dropdown of the
  // scheme's choices; everything else stays a free numeric field.
  const isEnum = () => props.type === 'enum' && props.choices?.length;
  // Enum values are chosen from a dropdown (leave untouched); a free numeric
  // value may be pasted with grouping (Excel) → normalise to the engine grammar.
  const submit = () => props.onSave(name(), isEnum() ? value() : parseNumericInput(value()));
  return (
    <div class="ky-var-edit">
      <Show when={props.isNew} fallback={<span class="ky-var-name">{props.name}</span>}>
        <input
          type="text" placeholder="Nama variabel (mis. Gaji Pokok)"
          value={name()} onInput={(e) => setName(e.currentTarget.value)}
        />
      </Show>
      <Show when={isEnum()} fallback={
        <input
          type="text" class="ky-var-value" placeholder="0.00" inputmode="decimal"
          value={value()} onInput={(e) => setValue(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        />
      }>
        <select class="kg-select" value={value()} onInput={(e) => setValue(e.currentTarget.value)}>
          <For each={props.choices}>{(c) => <option value={c}>{c}</option>}</For>
        </select>
      </Show>
      <button class="btn primary" onClick={submit}>Simpan</button>
      <button class="btn ghost" onClick={props.onCancel}>Batal</button>
    </div>
  );
}

// ApprovalEditor — HR sets an employee's lembur approval role + optional
// explicit approver (overrides the auto role+department routing).
function ApprovalEditor(props) {
  const [role, setRole] = createSignal('staff');
  const [approver, setApprover] = createSignal('');
  const [msg, setMsg] = createSignal(null);
  const [busy, setBusy] = createSignal(false);

  // Reset the form whenever a different employee is selected.
  createEffect(() => {
    setRole(props.employee.approvalRole || 'staff');
    setApprover(props.employee.approverNik || '');
    setMsg(null);
  });

  async function save() {
    setBusy(true);
    setMsg(null);
    const res = await fetch(`/api/employee/${encodeURIComponent(props.employee.nik)}/approval`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: role(), approverNik: approver().trim() }),
    });
    const data = await res.json().catch(() => ({}));
    setBusy(false);
    if (res.ok) { setMsg({ ok: true, t: 'Peran persetujuan tersimpan.' }); props.onSaved?.(); }
    else setMsg({ ok: false, t: data.error ?? `HTTP ${res.status}` });
  }

  return (
    <div class="ky-approval">
      <p class="ky-vars-note">
        <b>staff</b> mengajukan; <b>approver</b> (spv/manager) menyetujui staf
        sedepartemennya; <b>executive</b> (direksi) menyetujui approver. Approver
        eksplisit (opsional) memaksa jalur ke NIK tertentu, melewati routing
        otomatis. Tidak ada yang bisa menyetujui pengajuannya sendiri.
      </p>
      <div class="ky-approval-row">
        <label class="kg-field"><span>Peran</span>
          <select class="kg-select" value={role()} onInput={(e) => setRole(e.currentTarget.value)}>
            <option value="staff">staff</option>
            <option value="approver">approver (spv / manager)</option>
            <option value="executive">executive (direksi)</option>
          </select>
        </label>
        <label class="kg-field"><span>Approver eksplisit (opsional)</span>
          <input type="text" placeholder="NIK approver — kosong = otomatis"
            value={approver()} onInput={(e) => setApprover(e.currentTarget.value)} />
        </label>
        <button class="btn primary ky-approval-save" disabled={busy()} onClick={save}>Simpan</button>
      </div>
      <Show when={msg()}><div class={`kg-verdict ${msg().ok ? 'ok' : 'fail'}`}>{msg().t}</div></Show>
    </div>
  );
}

// ── Employee create/edit form ────────────────────────────────────────────
const RELIGIONS = ['Islam', 'Katolik', 'Kristen', 'Buddha', 'Hindu', 'Konghucu', 'Penghayat', 'TIDAK DIKETAHUI'];
const EDUCATIONS = ['TIDAK DIKETAHUI', 'SD', 'SMP', 'SMA', 'SMK', 'D1', 'D3', 'D4', 'S1', 'S2', 'S3'];
const MARITALS = ['Lajang', 'Menikah', 'Janda/Duda', 'Lainnya'];

// Keys match the Go Employee JSON tags exactly (note: scheme, NOT schemeCode).
function blankEmployee() {
  return {
    nik: '', name: '', status: 'Kontrak', active: true, allowLembur: null, fpCompanyId: 0, fid: 0, attendMode: 'MesinFinger',
    scheme: '', department: '', departmentCode: '', position: '', positionCode: '', divisionCode: '', slipTitle: '',
    hireDate: '', appointmentDate: '', contractStart: '', contractEnd: '', endDate: '',
    birthPlace: '', birthDate: '', gender: '', religion: '', maritalStatus: '', dependents: 0, citizenship: 'WNI', education: '',
    address: '', rt: '', rw: '', kelurahan: '', kecamatan: '', kota: '', kodePos: '', provinsi: '',
    phone: '', email: '', ktp: '', npwp: '', bpjsTk: '', bpjsKes: '', hasBpjsKes: false,
    bankName: '', bankAccount: '', bankHolder: '',
  };
}

function TextField(props) {
  return (
    <label class="kg-field">
      <span>{props.label}</span>
      <input type={props.type || 'text'} value={props.value ?? ''} disabled={props.disabled}
        placeholder={props.placeholder} inputmode={props.inputmode}
        onInput={(e) => props.onInput(e.currentTarget.value)} />
    </label>
  );
}

function SelectField(props) {
  // The options often come from an async resource (departments/positions/divisions),
  // so they don't exist on first render. A native <select> drops a value that matches
  // no option yet, and Solid won't re-apply value={props.value} when the options
  // arrive later (the value signal didn't change). Re-assert el.value via an effect
  // that also tracks the resolved option nodes, so the saved selection shows once the
  // options load. (Fixes edit-form fields not auto-populating from saved data.)
  let ref;
  const opts = children(() => props.children);
  createEffect(() => {
    opts();              // track: re-run when the option set changes
    const v = props.value ?? '';
    if (ref && ref.value !== v) ref.value = v;
  });
  return (
    <label class="kg-field">
      <span>{props.label}</span>
      <select ref={ref} class="kg-select" value={props.value ?? ''} onInput={(e) => props.onInput(e.currentTarget.value)}>
        {opts()}
      </select>
    </label>
  );
}

// ContractModal — quick contract renew/manage from the employee detail panel.
// Mirrors the Kontrak page's renew: prefills the next window (current end + 1 day
// → + 1 year, or today → + 1 year when there is none) and writes via the same
// endpoint. The current window is shown for context.
function ContractModal(props) {
  const e = props.employee;
  const baseStart = e.contractEnd ? addDaysISO(e.contractEnd, 1) : todayISO();
  const [start, setStart] = createSignal(baseStart);
  const [end, setEnd] = createSignal(addYearsISO(baseStart, 1));
  const [busy, setBusy] = createSignal(false);
  const [err, setErr] = createSignal('');

  async function save() {
    if (!start() || !end()) { setErr('Tanggal mulai dan akhir wajib diisi.'); return; }
    setBusy(true); setErr('');
    const res = await fetch(`/api/employee/${encodeURIComponent(e.nik)}/contract`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contractStart: start(), contractEnd: end() }),
    });
    const data = await res.json().catch(() => ({}));
    setBusy(false);
    if (res.ok) props.onSaved();
    else setErr(data.error || `HTTP ${res.status}`);
  }

  return (
    <div class="ky-modal-backdrop" onClick={(ev) => { if (ev.target === ev.currentTarget) props.onCancel(); }}>
      <div class="ky-modal" style="max-width:460px">
        <div class="ky-form-head">
          <h3 style="margin:0">Kelola Kontrak — {e.name}</h3>
          <div class="spacer" />
          <button class="btn ghost" disabled={busy()} onClick={props.onCancel}>Batal</button>
          <button class="btn primary" disabled={busy()} onClick={save}>{busy() ? 'Menyimpan…' : 'Perbarui'}</button>
        </div>
        <div class="ky-form-body">
          <Show when={err()}><div class="kg-verdict fail" style="margin-bottom:12px">{err()}</div></Show>
          <p class="kg-meta-text" style="margin-top:0">
            Kontrak saat ini: {e.contractStart || '—'} s/d {e.contractEnd || '—'}
            <Show when={e.contractEnd}> <ContractBadge date={e.contractEnd} /></Show>
          </p>
          <div class="ky-form-grid">
            <label class="kg-field"><span>Mulai kontrak</span>
              <input type="date" value={start()} onInput={(ev) => setStart(ev.currentTarget.value)} /></label>
            <label class="kg-field"><span>Akhir kontrak</span>
              <input type="date" value={end()} onInput={(ev) => setEnd(ev.currentTarget.value)} /></label>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmployeeForm(props) {
  const isEdit = props.mode === 'edit';
  // Edit inputs always bind the RAW value (per the money/format standard); only
  // display cells format. This form has no rupiah fields (salary lives in vars).
  const [f, setF] = createSignal({ ...blankEmployee(), ...(props.employee || {}) });
  const [msg, setMsg] = createSignal(null);
  const [busy, setBusy] = createSignal(false);
  const up = (k, v) => setF({ ...f(), [k]: v });

  const [depts] = createResource(() => getJSON('/api/departments').catch(() => []));
  const [positions] = createResource(() => getJSON('/api/positions').catch(() => []));
  const [divisions] = createResource(() => getJSON('/api/divisions').catch(() => []));
  const [schemes] = createResource(() => getJSON('/api/schemes').catch(() => []));
  // ALL active companies (reference list, not the admin's scope) — the fp
  // dropdown must offer every company a fingerprint machine can live in.
  const [companies] = createResource(() => getJSON('/api/companies/ref').catch(() => []));

  // Picking a department/position sets BOTH the code (the authoritative flag)
  // and the cached display name; department also pre-fills the scheme if empty.
  function pickDept(code) {
    const d = (depts() ?? []).find((x) => x.code === code);
    setF({ ...f(), departmentCode: code, department: d ? d.name : '',
      scheme: f().scheme || (d && d.schemeCode) || '' });
  }
  function pickPos(code) {
    const p = (positions() ?? []).find((x) => x.code === code);
    setF({ ...f(), positionCode: code, position: p ? p.name : '' });
  }

  async function save() {
    setBusy(true); setMsg(null);
    const body = { ...f(), dependents: Number(f().dependents) || 0, fpCompanyId: Number(f().fpCompanyId) || 0, fid: Number(f().fid) || 0 };
    const url = isEdit ? `/api/employee/${encodeURIComponent(f().nik)}` : '/api/employees';
    const res = await fetch(url, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    setBusy(false);
    if (res.ok) props.onSaved(f().nik);
    else setMsg(data.error ?? `HTTP ${res.status}`);
  }

  return (
    <div class="ky-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) props.onCancel(); }}>
      <div class="ky-modal">
        <div class="ky-form-head">
          <h3 style="margin:0">{isEdit ? `Edit ${f().name || f().nik}` : 'Karyawan baru'}</h3>
          <div class="spacer" />
          <button class="btn ghost" disabled={busy()} onClick={props.onCancel}>Batal</button>
          <button class="btn primary" disabled={busy()} onClick={save}>Simpan</button>
        </div>
        <div class="ky-form-body">
          <Show when={msg()}><div class="kg-verdict fail" style="margin-bottom:12px">{msg()}</div></Show>

          <div class="ky-form-sec">
            <h4>Identitas</h4>
            <div class="ky-form-grid">
              <TextField label="NIK" value={f().nik} disabled={isEdit} onInput={(v) => up('nik', v)} placeholder="NIK unik" />
              <TextField label="Nama" value={f().name} onInput={(v) => up('name', v)} />
              <SelectField label="Status" value={f().status} onInput={(v) => up('status', v)}>
                <option value="Kontrak">Kontrak</option><option value="Tetap">Tetap</option><option value="Keluarga">Keluarga</option>
              </SelectField>
              <SelectField label="Jenis Kelamin" value={f().gender} onInput={(v) => up('gender', v)}>
                <option value="">—</option><option value="Laki-laki">Laki-laki</option><option value="Perempuan">Perempuan</option>
              </SelectField>
              <TextField label="Tempat Lahir" value={f().birthPlace} onInput={(v) => up('birthPlace', v)} />
              <TextField label="Tanggal Lahir" type="date" value={f().birthDate} onInput={(v) => up('birthDate', v)} />
              <SelectField label="Agama" value={f().religion} onInput={(v) => up('religion', v)}>
                <option value="">—</option><For each={RELIGIONS}>{(x) => <option value={x}>{x}</option>}</For>
              </SelectField>
              <SelectField label="Status Pernikahan" value={f().maritalStatus} onInput={(v) => up('maritalStatus', v)}>
                <option value="">—</option><For each={MARITALS}>{(x) => <option value={x}>{x}</option>}</For>
              </SelectField>
              <TextField label="Tanggungan Anak" type="number" value={f().dependents} onInput={(v) => up('dependents', v)} />
              <SelectField label="Kewarganegaraan" value={f().citizenship} onInput={(v) => up('citizenship', v)}>
                <option value="WNI">WNI</option><option value="WNA">WNA</option>
              </SelectField>
              <SelectField label="Pendidikan" value={f().education} onInput={(v) => up('education', v)}>
                <option value="">—</option><For each={EDUCATIONS}>{(x) => <option value={x}>{x}</option>}</For>
              </SelectField>
            </div>
          </div>

          <div class="ky-form-sec">
            <h4>Kepegawaian</h4>
            <div class="ky-form-grid">
              <SelectField label="Departemen" value={f().departmentCode} onInput={pickDept}>
                <option value="">—</option>
                <For each={depts() ?? []}>{(d) => <option value={d.code}>{d.code} · {d.name}</option>}</For>
              </SelectField>
              <SelectField label="Posisi" value={f().positionCode} onInput={pickPos}>
                <option value="">—</option>
                <For each={positions() ?? []}>{(p) => <option value={p.code}>{p.code} · {p.name}</option>}</For>
              </SelectField>
              <SelectField label="Skema Gaji" value={f().scheme} onInput={(v) => up('scheme', v)}>
                <option value="">—</option>
                <For each={schemes() ?? []}>{(s) => <option value={s.code}>{s.code}</option>}</For>
              </SelectField>
              <SelectField label="Mesin sidik jari (perusahaan)"
                value={String(f().fpCompanyId || 0)} onInput={(v) => up('fpCompanyId', Number(v) || 0)}>
                <option value="0">— perusahaan sendiri —</option>
                <For each={companies() ?? []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
              </SelectField>
              <TextField label="FID (ID sidik jari)" type="number" inputmode="numeric"
                value={f().fid || ''} onInput={(v) => up('fid', v)} placeholder="ID mesin absen (kosong = belum terdaftar)" />
              <SelectField label="Mode Absen" value={f().attendMode || 'MesinFinger'} onInput={(v) => up('attendMode', v)}>
                <option value="MesinFinger">Mesin Finger (masuk &amp; pulang)</option>
                <option value="MesinFingerPagi">Mesin Finger Pagi (scan pagi saja)</option>
                <option value="Manual">Manual (hadir default)</option>
                <option value="NotApplicable">Tanpa absen (hadir default)</option>
              </SelectField>
              <SelectField label="Boleh Ajukan Lembur"
                value={f().allowLembur === true ? '1' : f().allowLembur === false ? '0' : ''}
                onInput={(v) => up('allowLembur', v === '' ? null : v === '1')}>
                <option value="">Ikuti departemen</option>
                <option value="1">Boleh</option>
                <option value="0">Tidak boleh</option>
              </SelectField>
              <SelectField label="Divisi" value={f().divisionCode} onInput={(v) => up('divisionCode', v)}>
                <option value="">—</option>
                <For each={divisions() ?? []}>{(d) => <option value={d.code}>{d.code} · {d.name}</option>}</For>
              </SelectField>
              <TextField label="Jabatan di Slip" value={f().slipTitle} onInput={(v) => up('slipTitle', v)} />
              <TextField label="Tgl Masuk" type="date" value={f().hireDate} onInput={(v) => up('hireDate', v)} />
              <TextField label="Tgl Pengangkatan" type="date" value={f().appointmentDate} onInput={(v) => up('appointmentDate', v)} />
              <TextField label="Kontrak Mulai" type="date" value={f().contractStart} onInput={(v) => up('contractStart', v)} />
              <TextField label="Kontrak Selesai" type="date" value={f().contractEnd} onInput={(v) => up('contractEnd', v)} />
              <TextField label="Tgl Keluar" type="date" value={f().endDate} onInput={(v) => up('endDate', v)} />
              <label class="ky-check ky-form-check"><input type="checkbox" checked={f().active} onInput={(e) => up('active', e.currentTarget.checked)} /> aktif</label>
            </div>
          </div>

          <div class="ky-form-sec">
            <h4>Kontak &amp; Alamat</h4>
            <div class="ky-form-grid">
              <TextField label="Alamat" value={f().address} onInput={(v) => up('address', v)} />
              <TextField label="RT" value={f().rt} onInput={(v) => up('rt', v)} />
              <TextField label="RW" value={f().rw} onInput={(v) => up('rw', v)} />
              <TextField label="Kelurahan" value={f().kelurahan} onInput={(v) => up('kelurahan', v)} />
              <TextField label="Kecamatan" value={f().kecamatan} onInput={(v) => up('kecamatan', v)} />
              <TextField label="Kota" value={f().kota} onInput={(v) => up('kota', v)} />
              <TextField label="Kode Pos" value={f().kodePos} onInput={(v) => up('kodePos', v)} />
              <TextField label="Provinsi" value={f().provinsi} onInput={(v) => up('provinsi', v)} />
              <TextField label="No. WhatsApp" value={f().phone} onInput={(v) => up('phone', v)} />
              <TextField label="Email" type="email" value={f().email} onInput={(v) => up('email', v)} />
            </div>
          </div>

          <div class="ky-form-sec">
            <h4>Dokumen &amp; BPJS</h4>
            <div class="ky-form-grid">
              <TextField label="No. KTP" value={f().ktp} onInput={(v) => up('ktp', v)} />
              <TextField label="NPWP" value={f().npwp} onInput={(v) => up('npwp', v)} />
              <TextField label="BPJS Ketenagakerjaan" value={f().bpjsTk} onInput={(v) => up('bpjsTk', v)} />
              <TextField label="BPJS Kesehatan" value={f().bpjsKes} onInput={(v) => up('bpjsKes', v)} />
              <label class="ky-check ky-form-check"><input type="checkbox" checked={f().hasBpjsKes} onInput={(e) => up('hasBpjsKes', e.currentTarget.checked)} /> dapat BPJS Kesehatan</label>
            </div>
          </div>

          <div class="ky-form-sec">
            <h4>Bank</h4>
            <div class="ky-form-grid">
              <TextField label="Nama Bank" value={f().bankName} onInput={(v) => up('bankName', v)} />
              <TextField label="Nomor Rekening" value={f().bankAccount} onInput={(v) => up('bankAccount', v)} />
              <TextField label="Atas Nama" value={f().bankHolder} onInput={(v) => up('bankHolder', v)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// SaldoModal — the ledger manager for one employee's balances. A dropdown picks
// a balance; its full transaction history shows below, with editable amounts and
// a form to insert a manual line. The payroll run posts here on finalize; manual
// edits/inserts adjust the running balance (validated non-negative server-side).
function SaldoModal(props) {
  const [balName, setBalName] = createSignal(props.initial || props.balances[0]?.name || '');
  const kind = () => (props.balances.find((b) => b.name === balName())?.kind) || '';
  const [data, { refetch }] = createResource(
    balName,
    (name) => name ? getJSON(`/api/employee/${encodeURIComponent(props.nik)}/balance/${encodeURIComponent(name)}/txns`) : null,
  );
  const [op, setOp] = createSignal('tambah');
  const [amount, setAmount] = createSignal('');
  const [effDate, setEffDate] = createSignal(todayISO());
  const [note, setNote] = createSignal('');
  const [msg, setMsg] = createSignal(null);

  async function add() {
    setMsg(null);
    const res = await fetch(`/api/employee/${encodeURIComponent(props.nik)}/balance/${encodeURIComponent(balName())}/txn`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ op: op(), amount: parseNumericInput(amount()), effDate: effDate(), note: note() }),
    });
    const d = await res.json().catch(() => ({}));
    if (res.ok) { setAmount(''); setNote(''); setMsg({ ok: true, msg: 'Transaksi ditambahkan.' }); refetch(); props.onChanged(); }
    else setMsg({ ok: false, msg: d.error || `HTTP ${res.status}` });
  }
  async function editAmt(id, value) {
    const res = await fetch(`/api/balance/txn/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ amount: value }),
    });
    const d = await res.json().catch(() => ({}));
    if (res.ok) { setMsg(null); refetch(); props.onChanged(); return true; }
    setMsg({ ok: false, msg: d.error || 'gagal menyimpan' });
    return false;
  }
  async function del(id) {
    if (!confirm('Hapus transaksi ini? Saldo akan dihitung ulang.')) return;
    const res = await fetch(`/api/balance/txn/${id}`, { method: 'DELETE' });
    const d = await res.json().catch(() => ({}));
    if (res.ok) { setMsg(null); refetch(); props.onChanged(); }
    else setMsg({ ok: false, msg: d.error || 'gagal menghapus' });
  }

  return (
    <div class="modal-backdrop" onClick={props.onClose}>
      <div class="modal-card card card-pad" onClick={(e) => e.stopPropagation()} style="max-width:760px">
        <div class="ky-vars-head">
          <h4 class="kg-hist-title">Kelola Saldo — {props.nik}</h4>
          <div class="spacer" />
          <button class="btn ghost" onClick={props.onClose}>✕</button>
        </div>

        <div class="pg-create">
          <label class="kg-field">
            <span>Saldo</span>
            <select class="kg-select" value={balName()} onInput={(e) => setBalName(e.currentTarget.value)}>
              <For each={props.balances}>{(b) => <option value={b.name}>{b.name} ({b.kind})</option>}</For>
            </select>
          </label>
          <div class="spacer" />
          <span class="kg-meta-text">Saldo berjalan: <b class="money">{formatNumber(data()?.saldo ?? '0')}</b></span>
        </div>

        <Show when={msg()}>
          <div class={`kg-verdict ${msg().ok ? 'ok' : 'fail'}`}>{msg().msg}</div>
        </Show>

        <table class="data">
          <thead>
            <tr><th>Tanggal</th><th>Operasi</th><th class="num">Jumlah</th><th class="num">Saldo</th><th>Sumber</th><th /></tr>
          </thead>
          <tbody>
            <For each={data()?.txns ?? []}>{(t) =>
              <tr>
                <td>{t.effDate}</td>
                <td><span class={`badge ${t.op === 'kurang' ? 'danger' : t.op === 'tambah' ? 'success' : 'info'}`}>{t.op}</span></td>
                <td class="num"><EditableAmount value={t.amount} onSave={(v) => editAmt(t.id, v)} /></td>
                <td class="num money">{formatNumber(t.after)}</td>
                <td class="kg-sub">{t.runId ? `Gaji #${t.runId}` : 'Manual'}<Show when={t.note}> · {t.note}</Show></td>
                <td><button class="btn ghost danger" title="Hapus" onClick={() => del(t.id)}>✕</button></td>
              </tr>
            }</For>
            <Show when={(data()?.txns ?? []).length === 0}>
              <tr><td colspan="6" class="kg-meta-text">Belum ada transaksi.</td></tr>
            </Show>
          </tbody>
        </table>

        <div class="ky-vars-head"><h4 class="kg-hist-title">Tambah transaksi manual</h4></div>
        <div class="pg-create">
          <label class="kg-field"><span>Operasi</span>
            <select class="kg-select" value={op()} onInput={(e) => setOp(e.currentTarget.value)}>
              <option value="set">Set saldo awal</option>
              <option value="tambah">Tambah</option>
              <option value="kurang">Kurang</option>
            </select>
          </label>
          <label class="kg-field"><span>Jumlah</span>
            <input class="pg-cell pg-cell-num" inputmode="decimal" placeholder="0" value={amount()}
              onInput={(e) => setAmount(e.currentTarget.value)} /></label>
          <label class="kg-field"><span>Tanggal</span>
            <input type="date" value={effDate()} onInput={(e) => setEffDate(e.currentTarget.value)} /></label>
          <label class="kg-field kg-field-grow"><span>Catatan</span>
            <input type="text" placeholder="opsional" value={note()} onInput={(e) => setNote(e.currentTarget.value)} /></label>
          <button class="btn primary" disabled={!amount()} onClick={add}>Tambah</button>
        </div>
        <p class="ky-vars-note">
          <b>{kind() === 'pinjaman' ? 'Pinjaman' : 'Simpanan'}</b>: posting pemrosesan gaji
          {kind() === 'pinjaman' ? ' mengurangi' : ' menambah'} saldo. Saldo tidak boleh negatif —
          jumlah yang melebihi batas akan ditolak.
        </p>
      </div>
    </div>
  );
}

// EditableAmount — an amount cell that becomes an input on focus; saves on
// blur/Enter when changed, reverts on rejection.
function EditableAmount(props) {
  const [val, setVal] = createSignal(props.value);
  createEffect(() => setVal(props.value));
  async function commit(e) {
    const next = parseNumericInput(val());
    if (next === props.value) return;
    const ok = await props.onSave(next);
    if (ok) setVal(next); else setVal(props.value);
  }
  return (
    <input class="pg-cell pg-cell-num" inputmode="decimal" value={val()}
      onInput={(e) => setVal(e.currentTarget.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      onBlur={commit} />
  );
}
